/**
 * Hook for managing the offline sync queue.
 * Handles pending operations, auto-sync on reconnect, and retry logic.
 */
import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/shared/utils/logger';
import { DB_CONFIG, dbOperation } from './offlineDb';
import type { OfflineOperation, OfflineOperationType } from './offlineDb';

export type { OfflineOperation, OfflineOperationType };

interface UseSyncQueueOptions {
  db: IDBDatabase | null;
  isOnline: boolean;
}

export function useSyncQueue({ db, isOnline }: UseSyncQueueOptions) {
  const [pendingOperations, setPendingOperations] = useState<OfflineOperation[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);

  // Wrapper for dbOperation that uses the current db instance
  const dbOp = useCallback(<T>(
    storeName: string,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> => {
    if (!db) throw new Error('Database not initialized');
    return dbOperation(db, storeName, mode, operation);
  }, [db]);

  // Add pending operation
  const addPendingOperation = useCallback(async (
    type: OfflineOperationType,
    data: any,
    module: string
  ): Promise<string> => {
    const operation: OfflineOperation = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      data: { ...data, offlineCreated: true },
      timestamp: Date.now(),
      attempts: 0,
      module
    };

    await dbOp(
      DB_CONFIG.stores.operations,
      'readwrite',
      (store) => store.add(operation)
    );

    setPendingOperations(prev => [...prev, operation]);
    return operation.id;
  }, [dbOp]);

  // Remove completed operation
  const removePendingOperation = useCallback(async (operationId: string) => {
    await dbOp(
      DB_CONFIG.stores.operations,
      'readwrite',
      (store) => store.delete(operationId)
    );

    setPendingOperations(prev => prev.filter(op => op.id !== operationId));
  }, [dbOp]);

  // Load pending operations from database
  const loadPendingOperations = useCallback(async () => {
    if (!db) return;

    const operations = await dbOp(
      DB_CONFIG.stores.operations,
      'readonly',
      (store) => store.getAll()
    );

    setPendingOperations(operations);
  }, [db, dbOp]);

  // Sync pending operations
  const syncPendingOperations = useCallback(async () => {
    if (!isOnline || isSyncing || pendingOperations.length === 0) return;

    setIsSyncing(true);

    try {
      const { offlineSyncService } = await import('@/core/offline/offlineSync');

      for (const operation of pendingOperations) {
        const validation = offlineSyncService.validateData(operation);
        if (!validation.valid) {
          logger.error(`Operazione ${operation.id} non valida: ${validation.error}`);
          await removePendingOperation(operation.id);
          continue;
        }

        if (!offlineSyncService.canRetryOperation(operation)) {
          logger.warn(`Operazione ${operation.id} ha raggiunto il limite massimo di tentativi`);
          continue;
        }

        try {
          logger.log(`Sincronizzando operazione ${operation.type}:`, operation.id);
          const result = await offlineSyncService.syncOperation(operation);

          if (result.success) {
            logger.log(`Operazione ${operation.id} sincronizzata con successo`);
            await removePendingOperation(operation.id);

            if (result.data?.offlineId && result.data?.id) {
              logger.log(`Mapping offline ID ${result.data.offlineId} -> ${result.data.id}`);
            }
          } else {
            throw new Error(result.error || 'Errore sconosciuto durante la sincronizzazione');
          }
        } catch (error: any) {
          logger.error(`Errore sincronizzazione operazione ${operation.id}:`, error);

          if (!offlineSyncService.isRecoverableError(error)) {
            logger.warn(`Errore non recuperabile per operazione ${operation.id}, rimozione dalla coda`);
            await removePendingOperation(operation.id);
            continue;
          }

          const updatedOperation = {
            ...operation,
            attempts: operation.attempts + 1,
            lastAttempt: Date.now()
          };

          await dbOp(
            DB_CONFIG.stores.operations,
            'readwrite',
            (store) => store.put(updatedOperation)
          );

          setPendingOperations(prev =>
            prev.map(op => op.id === operation.id ? updatedOperation : op)
          );

          const nextRetry = new Date(offlineSyncService.getNextRetryTime(operation));
          logger.log(`Prossimo tentativo per operazione ${operation.id}: ${nextRetry.toLocaleTimeString()}`);
        }
      }

      setLastSyncTime(Date.now());
      logger.log(`Sincronizzazione completata alle ${new Date().toLocaleTimeString()}`);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, pendingOperations, removePendingOperation, dbOp]);

  // Load pending operations when db is ready
  useEffect(() => {
    if (db) {
      loadPendingOperations();
    }
  }, [db, loadPendingOperations]);

  // Re-read from IDB on reconnect
  useEffect(() => {
    if (isOnline && db) {
      loadPendingOperations();
    }
  }, [isOnline, db, loadPendingOperations]);

  // Auto-sync when back online
  useEffect(() => {
    if (isOnline && pendingOperations.length > 0) {
      const timer = setTimeout(() => {
        syncPendingOperations();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, pendingOperations.length, syncPendingOperations]);

  return {
    pendingOperations,
    pendingCount: pendingOperations.length,
    isSyncing,
    lastSyncTime,
    addPendingOperation,
    removePendingOperation,
    syncPendingOperations,
  };
}
